import {Dialog, Pane} from 'evergreen-ui';
import React, {useCallback, useEffect} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {ProviderEvent} from './domain';
import {YesNoOption, YesNoOptionMap} from '../client/domain';
import {useConfirmTreatmentHoldEnd} from './useConfirmTreatmentHoldEnd';
import {formatDate} from '../../common/util/date';
import {parseISO} from 'date-fns';
import {ModalDatePicker} from '../client/form-components/ModalDatePicker';
import {ModalSelectionInput} from '../client/form-components/ModalSelectionInput';
import {useObservable} from '../../rxsupport';

type TreatmentHoldEndConfirmationForm = {
    holdEndDate: string;
    providerConfirmedResumeServices: YesNoOption | '';
};

type Props = {
    providerEvent: ProviderEvent | null;
    onClose: () => void;
    onSuccess: () => void;
};

const WIDTH = 600;

export function TreatmentHoldEndConfirmationModal({providerEvent, onClose, onSuccess}: Props) {
    const methods = useForm<TreatmentHoldEndConfirmationForm>({
        defaultValues: {
            holdEndDate: '',
            providerConfirmedResumeServices: '',
        },
    });

    const {execute: confirmTreatmentHoldEnd, isRunning$} = useConfirmTreatmentHoldEnd(() => {
        onSuccess();
        onClose();
    });
    const isSending = useObservable(isRunning$, false);

    useEffect(() => {
        if (providerEvent?.holdEndDate) {
            methods.reset({
                holdEndDate: providerEvent.holdEndDate,
                providerConfirmedResumeServices: '',
            });
        }
    }, [providerEvent, methods]);

    const holdEndDateLabel = providerEvent?.holdEndDate
        ? formatDate(parseISO(providerEvent.holdEndDate))
        : '';

    const onSubmit = useCallback(async (data: TreatmentHoldEndConfirmationForm) => {
        if (!providerEvent || !data.providerConfirmedResumeServices) {
            return;
        }
        await confirmTreatmentHoldEnd(providerEvent.guid, data.providerConfirmedResumeServices);
    }, [confirmTreatmentHoldEnd, providerEvent]);

    return (
        <Dialog
            title="Treatment Hold End Confirmation"
            isShown={providerEvent != null}
            width={WIDTH}
            hasFooter={false}
            onCloseComplete={onClose}
        >
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Pane paddingBottom={16}>
                        <ModalDatePicker<TreatmentHoldEndConfirmationForm>
                            label="Hold End Date"
                            field="holdEndDate"
                            disabled={true}
                            stacked={true}
                        />
                    </Pane>
                    <Pane paddingBottom={24}>
                        <ModalSelectionInput<TreatmentHoldEndConfirmationForm>
                            options={YesNoOptionMap}
                            label={`Will services resume on ${holdEndDateLabel}?`}
                            field="providerConfirmedResumeServices"
                            disabled={isSending}
                            labelPosition="top"
                        />
                    </Pane>
                    <Pane display="flex" justifyContent="flex-end">
                        <button
                            type="button"
                            className="px-5 py-2 mr-5 focus:outline-none"
                            disabled={isSending}
                            onClick={onClose}
                            style={{
                                backgroundColor: '#FFF',
                                color: '#D14343',
                                borderColor: '#D14343',
                                border: '1px solid',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-5 py-2 focus:outline-none ${isSending ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isSending}
                            style={{
                                backgroundColor: '#FFF',
                                color: '#00AB93',
                                borderColor: '#00AB93',
                                border: '1px solid',
                            }}
                        >
                            Submit
                        </button>
                    </Pane>
                </form>
            </FormProvider>
        </Dialog>
    );
}
